import type { Messages } from './en.js';

/** Français — terminologie alignée sur la landing (Chronologie, Doublons…). */
export const fr: Messages = {
  // ── nav ────────────────────────────────────────────────────────────────
  'nav.timeline': 'Chronologie',
  'nav.search': 'Recherche',
  'nav.duplicates': 'Doublons',
  'nav.collections': 'Collections',
  'nav.import': 'Import',
  'nav.trash': 'Corbeille',
  'nav.settings': 'Réglages',

  // ── common actions ─────────────────────────────────────────────────────
  'common.restore': 'Restaurer',
  'common.delete': 'Supprimer',
  'common.cancel': 'Annuler',
  'common.save': 'Enregistrer',
  'common.close': 'Fermer',
  'common.upgrade': 'Passer au niveau supérieur',
  'common.upgradeToPro': 'Passer à Pro',
  'common.search': 'Rechercher',
  'common.tryAgain': 'Réessayer',
  'common.loading': 'Chargement…',
  'common.back': 'Retour',
  'common.skip': 'Passer',
  'common.verify': 'Vérifier',
  'common.resend': 'Renvoyer',
  'common.free': 'Free',
  'common.pro': 'Pro',
  'common.clearFilters': 'Effacer les filtres',
  'common.addToChrome': 'Ajouter à Chrome',
  'toast.genericError': 'Un problème est survenu. Réessayez.',

  // ── plural units ───────────────────────────────────────────────────────
  'unit.tabs.one': '{count} onglet',
  'unit.tabs.few': '{count} onglets',
  'unit.tabs.many': '{count} onglets',
  'unit.tabs.other': '{count} onglets',
  'unit.sessions.one': '{count} session',
  'unit.sessions.few': '{count} sessions',
  'unit.sessions.many': '{count} sessions',
  'unit.sessions.other': '{count} sessions',
  'unit.items.one': '{count} élément',
  'unit.items.few': '{count} éléments',
  'unit.items.many': '{count} éléments',
  'unit.items.other': '{count} éléments',
  'unit.entries.one': '{count} entrée',
  'unit.entries.few': '{count} entrées',
  'unit.entries.many': '{count} entrées',
  'unit.entries.other': '{count} entrées',
  'unit.copies.one': '{count} copie',
  'unit.copies.few': '{count} copies',
  'unit.copies.many': '{count} copies',
  'unit.copies.other': '{count} copies',
  'unit.browsers.one': '{count} navigateur',
  'unit.browsers.few': '{count} navigateurs',
  'unit.browsers.many': '{count} navigateurs',
  'unit.browsers.other': '{count} navigateurs',

  // ── app shell ──────────────────────────────────────────────────────────
  'shell.searchPlaceholder': 'Rechercher dans votre vault…',
  'shell.saved': 'SAUVEGARDÉ',
  'shell.toggleTheme': 'Changer de thème',

  // ── timeline ───────────────────────────────────────────────────────────
  'timeline.empty.title': 'Votre vault est vide — pour l’instant',
  'timeline.empty.body':
    'Balayez une fenêtre avec l’extension Nomad Tabs, ou sauvez votre vieux cimetière OneTab. Tout atterrit ici, et tout est cherchable.',
  'timeline.empty.import': 'Importer vos onglets',
  'timeline.empty.importOneTab': 'Importer depuis OneTab',
  'timeline.empty.tip': 'ASTUCE : ⇧⌘S BALAIE LA FENÊTRE ACTIVE DEPUIS L’EXTENSION',
  'timeline.noMatch.title': 'Aucune session ne correspond à ces filtres',
  'timeline.noMatch.body': 'Élargissez la période, choisissez un autre navigateur ou désactivez « avec contenu uniquement ».',
  'timeline.loadOlder': 'Charger les sessions plus anciennes',
  'timeline.toast.sessionDeleted': 'Session supprimée',
  'timeline.toast.loadFailed': 'Impossible de charger cette session',
  'timeline.delete.title': 'Supprimer cette session ?',
  'timeline.delete.subtitle': 'Supprime « {title} » et ses {tabs} sauvegardés. Cette action est irréversible.',
  'timeline.delete.body': 'Si vous changez d’avis, vous pourrez encore retrouver ses onglets dans l’historique du navigateur.',
  'timeline.delete.confirm': 'Supprimer la session',
  'timeline.delete.fallbackTitle': 'cette session',
  'timeline.offline.title': 'Vous êtes hors ligne',
  'timeline.offline.body': 'Votre vault fonctionne en local — la synchronisation reprendra d’elle-même dès votre retour en ligne.',
  'timeline.error.title': 'Impossible de charger votre chronologie',
  'timeline.error.body': 'Un problème est survenu en joignant le vault. Vérifiez votre connexion et réessayez.',

  // ── search ─────────────────────────────────────────────────────────────
  'search.mode.text': 'Texte',
  'search.mode.hybrid': 'Hybride',
  'search.mode.semantic': 'Sémantique',
  'search.placeholder': 'Rechercher dans votre vault…',
  'search.clear': 'Effacer la recherche',
  'search.status.counted': 'RECHERCHE DANS {count} ONGLETS',
  'search.status.default': 'RECHERCHE DANS VOTRE VAULT',
  'search.empty.title': 'Cherchez dans tout ce que vous avez sauvegardé',
  'search.empty.body':
    'Retrouvez un onglet par son titre, son adresse ou les mots de la page. Le mode sémantique le retrouve par le sens.',
  'search.noMatch.title': 'Aucun résultat pour « {q} »',
  'search.noMatch.body': 'Essayez moins de mots ou d’autres mots, passez en mode Hybride, ou effacez vos filtres.',
  'search.failed.eyebrow': '— ÉCHEC DE LA RECHERCHE',
  'search.failed.body': 'Un problème est survenu en joignant votre vault.',
  'search.failed.retryHint': 'Faites un nouvel essai.',
  'search.results': '{count} RÉSULTATS',
  'search.sort.relevance': 'Pertinence',
  'search.sort.date': 'Date',
  'search.loadMore': 'Charger plus de résultats',
  'search.bestMatch': 'MEILLEUR RÉSULTAT',
  'search.openedIn': 'OUVERT DANS {browser}',
  'search.facet.browser': 'NAVIGATEUR',
  'search.facet.date': 'DATE',
  'search.facet.topDomains': 'DOMAINES FRÉQUENTS',
  'search.range.any': 'Toutes dates',
  'search.range.24h': '24 heures',
  'search.range.7d': '7 jours',
  'search.range.30d': '30 jours',
  'search.range.1y': '12 mois',
  'search.upsell.eyebrow': 'RECHERCHE SÉMANTIQUE · PRO',
  'search.upsell.title': 'La recherche sémantique est une fonction Pro',
  'search.upsell.body':
    'Retrouvez vos onglets par le sens, pas seulement par mots-clés — « cet article sur la baisse des taux de la banque centrale » plutôt que le titre exact. Passez à Pro pour chercher dans tout ce que vous avez sauvegardé.',
  'search.upsell.seePlans': 'Voir les offres Pro',
  'search.upsell.useText': 'Chercher en mode texte',

  // ── duplicates ─────────────────────────────────────────────────────────
  'dupes.title': 'Centre des doublons',
  'dupes.summary.duplicates': '{count} DOUBLONS',
  'dupes.summary.ram': '≈ {ram} DE RAM À LIBÉRER',
  'dupes.closeSelected': 'Fermer la sélection ({count})',
  'dupes.keepNewest': 'Garder la plus récente, fermer le reste',
  'dupes.error.title': 'Impossible de charger vos doublons',
  'dupes.toast.closed': '{copies} fermées',
  'dupes.toast.closedBody': 'La copie la plus récente reste ouverte — chaque copie reste sauvegardée dans votre vault.',

  // ── collections ────────────────────────────────────────────────────────
  'collections.title': 'Collections',
  'collections.subtitle': 'Triées par l’IA — renommez ou fusionnez quand vous voulez',
  'collections.new': 'Nouvelle collection',
  'collections.banner.title': 'Les collections automatiques par IA sont une fonction Pro.',
  'collections.banner.body': 'Vous pouvez toujours créer et organiser vos collections à la main — c’est gratuit, pour toujours.',
  'collections.empty.title': 'Pas encore de collections',
  'collections.empty.body':
    'Regroupez vos onglets par thème. Créez-en une à la main — Pro classe aussi votre vault en collections automatiquement.',
  'collections.error.title': 'Impossible de charger vos collections',

  // ── import ─────────────────────────────────────────────────────────────
  'import.title': 'Importez vos vieux cimetières d’onglets',
  'import.drop.title': 'Déposez votre fichier d’export ici',
  'import.drop.body': 'OneTab, Session Buddy ou HTML de favoris —',
  'import.drop.browse': 'parcourir les fichiers',
  'import.readyEyebrow': 'PRÊT À IMPORTER',

  // ── settings ───────────────────────────────────────────────────────────
  'settings.title': 'Réglages',
  'settings.section.account': 'COMPTE & SYNC',
  'settings.section.browsers': 'NAVIGATEURS CONNECTÉS',
  'settings.section.capture': 'RÈGLES DE CAPTURE',
  'settings.section.sharing': 'PARTAGE',
  'settings.section.storage': 'STOCKAGE',
  'settings.section.appearance': 'APPARENCE',
  'settings.section.language': 'LANGUE',
  'settings.section.about': 'À PROPOS',
  'settings.section.danger': 'ZONE DE DANGER',
  'settings.account.private': 'PRIVÉ & SÉCURISÉ',
  'settings.account.yourAccount': 'Votre compte',
  'settings.account.proPlan': 'OFFRE PRO',
  'settings.account.freePlan': 'OFFRE FREE',
  'settings.account.managePlan': 'Gérer l’offre',
  'settings.account.seePro': 'Découvrir Pro',
  'settings.account.privacyNote':
    'Vos pages sauvegardées restent privées — vous seul pouvez les voir. Nous les gardons en sécurité, hors de portée de tous les autres.',
  'settings.account.billing': 'Facturation & paiements',
  'settings.browsers.syncAuto': 'Les autres se synchronisent tout seuls',
  'settings.browsers.thisBrowser': 'Ce navigateur',
  'settings.browsers.webApp': 'APP WEB',
  'settings.browsers.activeNow': 'ACTIF MAINTENANT',
  'settings.browsers.installHint':
    'Installez l’extension dans vos autres navigateurs — ils apparaîtront ici et se synchroniseront automatiquement.',
  'settings.capture.onDevice': 'SUR CET APPAREIL',
  'settings.capture.saveContent': 'Enregistrer le contenu des pages pour la recherche',
  'settings.capture.saveContentDesc': 'Texte uniquement — stocké en privé pour la recherche plein texte',
  'settings.capture.skipIncognito': 'Ignorer la navigation privée',
  'settings.capture.neverOn': 'Ne jamais capturer de contenu sur :',
  'settings.capture.addSite': '+ ajouter un site',
  'settings.capture.localNote':
    'Pour l’instant, ces règles vivent dans ce navigateur — la synchronisation entre appareils arrive bientôt.',
  'settings.sharing.readOnly': 'Liens publics en lecture seule',
  'settings.sharing.collection': 'Collection',
  'settings.sharing.sweep': 'Balayage',
  'settings.sharing.selectCollection': 'Choisir une collection…',
  'settings.sharing.selectSweep': 'Choisir un balayage…',
  'settings.sharing.createLink': 'Créer un lien',
  'settings.sharing.empty.title': 'Pas encore de liens de partage',
  'settings.sharing.empty.body': 'Créez un lien public vers une collection ou un balayage — en lecture seule.',
  'settings.sharing.copyLink': 'Copier le lien',
  'settings.sharing.revoke': 'Révoquer',
  'settings.toast.linkCreated': 'Lien créé',
  'settings.toast.linkCreatedBody': 'Toute personne disposant du lien peut le consulter — en lecture seule.',
  'settings.toast.linkFailed': 'Impossible de créer le lien',
  'settings.toast.linkFailedBody': 'Réessayez dans un instant.',
  'settings.toast.linkRevoked': 'Lien révoqué',
  'settings.toast.linkRevokedBody': 'Ce lien ne s’ouvre plus.',
  'settings.toast.revokeFailed': 'Impossible de révoquer le lien',
  'settings.toast.revokeFailedBody': 'Réessayez.',
  'settings.toast.copied': 'Copié',
  'settings.toast.copiedBody': 'Le lien est dans votre presse-papiers.',
  'settings.toast.deleteFailed': 'Échec de la suppression',
  'settings.toast.deleteFailedBody': 'Impossible de supprimer le compte. Réessayez.',
  'settings.danger.export': 'Exporter toutes les données',
  'settings.danger.deleteAccount': 'Supprimer compte & données',
  'settings.danger.note': 'Exportez d’abord — la suppression devient définitive après 14 jours.',
  'settings.storage.saved': '{count} sauvegardés',
  'settings.storage.tabs': 'ONGLETS',
  'settings.storage.sessions': 'SESSIONS',
  'settings.storage.indexed': 'INDEXÉS',
  'settings.appearance.dark': 'Thème sombre',
  'settings.appearance.darkDesc': 'Bleu nuit profond — le look par défaut',
  'settings.language.label': 'Langue de l’interface',
  'settings.language.desc': 'S’applique immédiatement, partout dans l’app.',
  'settings.promo.title': 'Installez l’extension',
  'settings.promo.body': 'Balayez une fenêtre entière en un clic, directement depuis votre barre d’outils.',
  'settings.about.webApp': 'App web',
  'settings.about.extension': 'Extension',
  'settings.deleteModal.title': 'Supprimer compte & données',
  'settings.deleteModal.subtitle': 'Votre vault sera supprimé partout.',
  'settings.deleteModal.body':
    'Chaque balayage, page sauvegardée et collection sera programmé pour suppression. Tout reste récupérable pendant 14 jours, puis disparaît pour de bon. Exportez d’abord vos données si vous pourriez en avoir besoin.',
  'settings.deleteModal.keep': 'Garder le compte',
  'settings.deleteModal.confirm': 'Tout supprimer',
  'settings.planModal.title': 'Gérer votre offre Pro',
  'settings.planModal.subtitle': 'Free reste généreux — Pro connecte tout.',
  'settings.planModal.body':
    'Annulez quand vous voulez : Pro reste actif jusqu’à la fin de la période payée. Ensuite : sauvegardes locales illimitées, synchronisation limitée à un navigateur, recherche IA et collections en pause.',
  'settings.planModal.nothingDeleted': 'Rien n’est supprimé.',
  'settings.planModal.emailSupport': 'Écrire au support pour annuler',
  'settings.planModal.keepPro': 'Garder Pro',

  // ── login / verify ─────────────────────────────────────────────────────
  'login.welcome': 'Bienvenue sur Nomad Tabs',
  'login.tagline': 'Un seul vault pour tous vos navigateurs.',
  'login.emailPlaceholder': 'vous@email.com',
  'login.sendLink': 'Envoyer le lien magique',
  'login.error.tooMany': 'Trop de demandes — patientez une minute et réessayez.',
  'login.error.sendFailed': 'Impossible d’envoyer le lien pour le moment. Réessayez dans un instant.',
  'login.reassure': 'Vos pages sauvegardées restent privées — vous seul pouvez ouvrir votre vault.',
  'login.checkInbox': 'Vérifiez votre boîte mail',
  'login.sentTo': 'Nous avons envoyé un lien de connexion à',
  'login.expires': 'LE LIEN EXPIRE DANS 15 MIN',
  'login.nothingThere': 'Rien reçu ?',
  'login.checkSpam': 'vérifiez les spams',
  'login.wrongAddress': '← Mauvaise adresse ? Revenir',
  'login.devDivider': 'DEV · COLLEZ LE TOKEN',
  'login.tokenPlaceholder': 'token de la console API',
  'verify.signingIn': 'Connexion en cours…',
  'verify.hangTight': 'Un instant, cela ne prend qu’une seconde.',
  'verify.expired.title': 'Ce lien a expiré',
  'verify.expired.body':
    'Les liens magiques valent 15 minutes et un seul usage. Demandez-en un nouveau et vous serez connecté aussitôt.',
  'verify.backToSignIn': 'Retour à la connexion',

  // ── onboarding ─────────────────────────────────────────────────────────
  'onboarding.hero.title1': 'Votre navigateur a enfin',
  'onboarding.hero.title2': 'de la mémoire.',
  'onboarding.hero.body':
    'Balayez des fenêtres entières dans votre vault, fermez-les sans crainte et retrouvez n’importe quelle page — grâce à ce qui y était écrit.',
  'onboarding.getStarted': 'Commencer',
  'onboarding.hero.micro': '60 SECONDES CHRONO · AUCUN COMPTE REQUIS POUR EXPLORER',
  'onboarding.perm.title': 'Pourquoi votre navigateur demande un accès',
  'onboarding.perm.lede': 'Le prochain message fait peur. Voici ce qu’il veut vraiment dire.',
  'onboarding.perm.head': '« LIRE ET MODIFIER TOUTES VOS DONNÉES SUR TOUS LES SITES » — TRADUCTION',
  'onboarding.perm.p1.title': 'Nous lisons les pages uniquement pour les rendre cherchables',
  'onboarding.perm.p1.body': 'Pour retrouver plus tard « cet article sur les taux immobiliers ».',
  'onboarding.perm.p2.title': 'Vos pages restent privées sur votre appareil',
  'onboarding.perm.p2.body': 'Vous seul pouvez ouvrir votre vault — nous ne voyons pas ce que vous sauvegardez.',
  'onboarding.perm.p3.title': 'Banques, e-mail et sites privés peuvent être exclus',
  'onboarding.perm.p3.body': 'Une seule liste dans les Réglages — le contenu n’y est jamais capturé.',
  'onboarding.perm.continue': 'Ça semble honnête — continuer',
  'onboarding.perm.later': 'Me demander plus tard',
  'onboarding.perm.micro': 'MODIFIABLE À TOUT MOMENT DANS LES RÉGLAGES',
  'onboarding.rescue.title': 'Sauvez vos onglets tout de suite',
  'onboarding.rescue.lede': 'Choisissez — les deux prennent moins d’une minute, et rien ne se perd.',
  'onboarding.rescue.onetab.title': 'Importer depuis OneTab',
  'onboarding.rescue.onetab.body':
    'Rapatriez tout votre cimetière OneTab en un clic — les groupes deviennent des sessions.',
  'onboarding.rescue.chooseFile': 'Choisir un fichier',
  'onboarding.rescue.onetab.foot': 'AUSSI : SESSION BUDDY · HTML DE FAVORIS',
  'onboarding.rescue.ext.title': 'Installez l’extension navigateur',
  'onboarding.rescue.ext.body':
    'Balayez une fenêtre entière en un clic, depuis votre barre d’outils. Regardez votre RAM revenir.',
  'onboarding.rescue.ext.foot': 'CHROME · EDGE · YANDEX · COMET',
  'onboarding.rescue.openApp': 'Ouvrir l’app web →',

  // ── pricing ────────────────────────────────────────────────────────────
  'pricing.title': 'Passez à Pro quand vous êtes prêt',
  'pricing.subtitle': 'Free reste généreux. Pro connecte tout.',
  'pricing.monthly': 'Mensuel ${price}',
  'pricing.yearly': 'Annuel ${price}',
  'pricing.forever': 'POUR TOUJOURS',
  'pricing.yourPlan': 'VOTRE OFFRE',
  'pricing.mostLoved': 'LE PLUS AIMÉ',
  'pricing.perMonth': '/MOIS',
  'pricing.perMonthYearly': '/MOIS · ${total} PAR AN',
  'pricing.free.f1': 'Sauvegardes locales illimitées',
  'pricing.free.f2': '1 navigateur synchronisé au cloud',
  'pricing.free.f3': 'Recherche par titre & URL',
  'pricing.free.f4': 'Recherche plein texte & IA',
  'pricing.free.f5': 'Collections IA',
  'pricing.pro.f1': 'Tous les navigateurs, tous les appareils synchronisés',
  'pricing.pro.f2': 'Plein texte + recherche sémantique IA',
  'pricing.pro.f3': 'Des collections IA qui se rangent toutes seules',
  'pricing.pro.f4': 'Historique des versions des pages sauvegardées',
  'pricing.pro.f5': 'Support prioritaire',
  'pricing.stayFree': 'Rester sur Free',
  'pricing.youreOnPro': 'Vous êtes sur Pro',
  'pricing.signInToUpgrade': 'Connectez-vous pour passer à Pro',
  'pricing.checkoutSoon': 'Le paiement ouvre bientôt — nous vous écrirons dès que c’est en ligne.',
  'pricing.checkoutFailed': 'Un problème est survenu au démarrage du paiement. Réessayez.',
  'pricing.footer': 'ANNULATION À TOUT MOMENT · EXPORT DES DONNÉES QUAND VOUS VOULEZ · LES SAUVEGARDES LOCALES NE SONT JAMAIS VERROUILLÉES',

  // ── billing / version history ──────────────────────────────────────────
  'billing.title': 'Facturation & paiements',
  'versions.title': 'Historique des versions',
  'versions.current': 'ACTUELLE',
  'versions.restoreThis': 'Restaurer cette version',

  // ── trash ──────────────────────────────────────────────────────────────
  'trash.title': 'Corbeille',
  'trash.autoPurge': 'PURGE AUTOMATIQUE APRÈS 14 JOURS',
  'trash.itemsLoading': '… ÉLÉMENTS',
  'trash.restoreSelected': 'Restaurer la sélection ({count})',
  'trash.restoring': 'Restauration…',
  'trash.emptyTrash': 'Vider la corbeille',
  'trash.error.title': 'Impossible de charger la corbeille',
  'trash.emptyState.title': 'La corbeille est vide',
  'trash.emptyState.body':
    'Les sessions et pages supprimées attendent ici 14 jours, puis sont purgées automatiquement.',
  'trash.footNote': 'SUPPRIMER DE LA CORBEILLE EST DÉFINITIF · EXPORT DES DONNÉES DANS RÉGLAGES → ZONE DE DANGER',
  'trash.purge.title': 'Supprimer pour toujours ?',
  'trash.purge.subtitle': '« {title} » sera supprimée définitivement — sans les 14 jours de récupération.',
  'trash.purge.confirm': 'Supprimer pour toujours',
  'trash.purge.sweepBody': 'Cela supprime définitivement la session et ses {tabs}.',
  'trash.purge.pageBody': 'Cela supprime définitivement la page sauvegardée et son contenu capturé.',
  'trash.emptyConfirm.title': 'Vider la corbeille ?',
  'trash.emptyConfirm.subtitle': 'Supprime définitivement {entries}.',
  'trash.emptyConfirm.body':
    'Tout le contenu de la corbeille disparaît pour de bon — c’est irréversible. Vous pouvez exporter vos données à tout moment dans Réglages → Zone de danger.',
  'trash.toast.restored': 'Restauré',
  'trash.toast.restoredBody': '« {title} » est de retour dans votre vault.',
  'trash.toast.restoredMany': '{entries} restaurées',
  'trash.toast.restoreFailed': 'Restauration impossible',
  'trash.toast.restoreManyFailed': 'Impossible de restaurer la sélection',
  'trash.toast.purged': 'Supprimé pour toujours',
  'trash.toast.purgedBody': '« {title} » a disparu pour de bon.',
  'trash.toast.purgeFailed': 'Suppression impossible',
  'trash.toast.emptied': 'Corbeille vidée',
  'trash.toast.emptiedBody': 'Tout le contenu de la corbeille a été supprimé définitivement.',
  'trash.toast.emptyFailed': 'Impossible de vider la corbeille',
};
